/* ============================================================
 * c-bundle-conflict
 * カート内で同梱禁止コレクション同士、または特殊コレクション × 通常商品が
 * 混在した場合に警告ポップアップを表示し、チェックアウトをブロックする。
 *
 * トリガー:
 *  ① DOMContentLoaded 時 (カートページ表示)
 *  ② cart:updated / cart:refresh イベント (商品追加・削除・ミニカート再描画)
 *  ③ form submit (チェックアウトボタン押下時の保険)
 *
 * 制御対象:
 *  - button[name="checkout"]    (main-cart, mini-cart)
 *  - .additional-checkout-buttons (Shop Pay 等の Express Checkout)
 * ============================================================ */
(function () {
  if (window.customElements.get('c-bundle-conflict-modal')) return;

  const REGULAR_GROUP_KEY = '__regular__';
  const REGULAR_GROUP_NAME = '通常商品';
  const SHOWN_SIGNATURE_STORAGE_KEY = 'c-bundle-conflict:shown-signature';

  /**
   * Shopify CDN の画像URLに width クエリパラメータを付与し、縮小版を取得する。
   * 例: https://cdn.shopify.com/.../foo.jpg?v=123 -> ?v=123&width=96
   */
  function withImageWidth(url, size) {
    if (!url) return '';
    const sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + size;
  }

  // バナー挿入対象 (カートページ + ミニカートドロワー)
  // main-cart.liquid の schema には "class" 指定がなく `.shopify-section--main-cart` は付与されないため、
  // page_type 判定 + #main 内の .container.container--medium を狙う方式に統一。
  const BANNER_TARGETS = [
    {
      name: 'cart-page',
      resolve: () => {
        // 1. main-cart.liquid に block "bundle_conflict_banner" が配置されていれば、
        //    [data-c-bundle-conflict-banner-mount] をターゲットにする (推奨経路)
        const mount = document.querySelector('[data-c-bundle-conflict-banner-mount]');
        if (mount) return mount;

        // 2. フォールバック: ブロック未配置時はカートページの #main 直下に挿入
        const settings = window.themeVariables && window.themeVariables.settings;
        if (!settings || settings.pageType !== 'cart') return null;
        return document.querySelector('#main .container.container--medium');
      },
    },
    {
      name: 'mini-cart',
      resolve: () => document.querySelector('#mini-cart .drawer__content'),
    },
  ];

  class CBundleConflictModal extends HTMLElement {
    connectedCallback() {
      try {
        this.groups = JSON.parse(this.dataset.groups || '[]');
      } catch (_) {
        this.groups = [];
      }
      try {
        this.excludeProductIds = JSON.parse(this.dataset.excludeProductIds || '[]');
      } catch (_) {
        this.excludeProductIds = [];
      }
      this.checkoutDisabledLabel = this.dataset.checkoutDisabledLabel || '';
      this.conflictListLabel = this.dataset.conflictListLabel || '';
      this.designMode = this.dataset.designMode === 'true';
      this.enableInEditor = this.dataset.enableInEditor === 'true';
      this.enableGa4Events = this.dataset.enableGa4Events === 'true';
      // !== 'false' (not === 'true') にすることで、属性が無い場合 → true (=ブロック有効) となり、
      // テーマアップデート前のキャッシュやロールバック時にも安全側のデフォルト挙動になる。
      this.enableCheckoutBlock = this.dataset.enableCheckoutBlock !== 'false';
      this.productImageSize = this.dataset.productImageSize || 'natural';
      this.openedSignature = null;
      this.lastViolated = false;

      this.bindCloseHandlers();
      this.bindKeyboardHandler();
      this.bindCartEvents();
      this.bindCheckoutSubmitGuard();

      this.scheduleCheck();
    }

    bindCloseHandlers() {
      this.addEventListener('click', (event) => {
        const target = event.target;
        if (
          target.matches('[data-action="close"]') ||
          target.closest('[data-action="close"]') ||
          target.classList.contains('modal__overlay')
        ) {
          event.preventDefault();
          this.close();
        }
      });
    }

    bindKeyboardHandler() {
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
        }
      });
    }

    bindCartEvents() {
      document.addEventListener('cart:updated', () => this.scheduleCheck());
      document.documentElement.addEventListener('cart:refresh', () => this.scheduleCheck(150));
      document.addEventListener('openable-element:open', (event) => {
        const id = event.target && event.target.id;
        if (id === 'mini-cart') this.scheduleCheck();
      });
    }

    bindCheckoutSubmitGuard() {
      document.addEventListener(
        'submit',
        (event) => {
          if (!this.lastViolated) return;
          const form = event.target;
          if (!(form instanceof HTMLFormElement)) return;
          if (!form.querySelector('button[name="checkout"], input[name="checkout"]')) return;
          // パススルーモード: 送信を妨げず、bypass 計測のみ行う
          if (!this.enableCheckoutBlock) {
            this.handleBypassedCheckout('submit');
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          this.handleBlockedCheckout('submit');
        },
        true
      );
      document.addEventListener(
        'click',
        (event) => {
          if (!this.lastViolated) return;
          const btn = event.target.closest && event.target.closest('button[name="checkout"]');
          if (!btn) return;
          // パススルーモード: 押下を妨げず、bypass 計測のみ行う
          if (!this.enableCheckoutBlock) {
            this.handleBypassedCheckout('click');
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          this.handleBlockedCheckout('click');
        },
        true
      );
    }

    handleBlockedCheckout(trigger) {
      this.openedSignature = null;
      this.open(this.lastResult);
      this.sendGA4Event(
        'bundle_conflict_blocked_checkout',
        Object.assign({ trigger: trigger }, this.lastViolatedPayload || {})
      );
    }

    handleBypassedCheckout(trigger) {
      // パススルーモード時、違反状態でチェックアウトに進んだことを計測する。
      // ブロックは行わない (呼び出し側が preventDefault しない)。
      // モーダルは自動で開かない (バナーから手動で開ける状態は維持)。
      // 1回のチェックアウト操作で click → submit の順に2イベントが発火するため、
      // ワンショットフラグで重複計測を防ぐ (1秒間)。
      if (this._bypassFired) return;
      this._bypassFired = true;
      setTimeout(() => { this._bypassFired = false; }, 1000);

      this.sendGA4Event(
        'bundle_conflict_bypassed_checkout',
        Object.assign({ trigger: trigger }, this.lastViolatedPayload || {})
      );
    }

    scheduleCheck(delay) {
      clearTimeout(this._checkTimer);
      this._checkTimer = setTimeout(() => this.checkAndApply(), delay || 0);
    }

    async checkAndApply() {
      if (this.designMode && !this.enableInEditor) return;
      if (!this.groups.length) return;

      let cart;
      try {
        const cartUrl =
          (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.cartUrl) ||
          '/cart';
        const res = await fetch(`${cartUrl}.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        cart = await res.json();
      } catch (_) {
        return;
      }

      const result = this.detectViolation(cart);
      const wasViolated = this.lastViolated;
      this.lastViolated = result.violated;
      this.lastResult = result;
      this.lastCartTotal = cart.total_price;
      this.applyCheckoutLock(result.violated);
      this.applyBannerState(result.violated);

      if (result.violated) {
        // 自動表示判定:
        //  - 同一インスタンスで未表示 (openedSignature !== signature)
        //  - sessionStorage にも未記録 (= 別ページで一度も自動表示されていない)
        const storedSignature = this.getStoredSignature();
        const shouldAutoShow =
          this.openedSignature !== result.signature && storedSignature !== result.signature;

        if (shouldAutoShow) {
          this.openedSignature = result.signature;
          this.setStoredSignature(result.signature);
          this.open(result);
          this.sendGA4Event('bundle_conflict_shown', this.buildEventPayload(result, cart));
        } else if (this.hasAttribute('open')) {
          this.renderConflictList(result);
        }
      } else {
        if (wasViolated && this.lastViolatedPayload) {
          this.sendGA4Event('bundle_conflict_resolved', {
            previous_groups: this.lastViolatedPayload.groups,
          });
        }
        this.openedSignature = null;
        this.clearStoredSignature();
        if (this.hasAttribute('open')) this.close();
      }

      if (result.violated) {
        this.lastViolatedPayload = this.buildEventPayload(result, cart);
      }
    }

    getStoredSignature() {
      try {
        return window.sessionStorage && window.sessionStorage.getItem(SHOWN_SIGNATURE_STORAGE_KEY);
      } catch (_) {
        return null;
      }
    }

    setStoredSignature(signature) {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.setItem(SHOWN_SIGNATURE_STORAGE_KEY, signature);
        }
      } catch (_) {}
    }

    clearStoredSignature() {
      try {
        if (window.sessionStorage) {
          window.sessionStorage.removeItem(SHOWN_SIGNATURE_STORAGE_KEY);
        }
      } catch (_) {}
    }

    /**
     * カートページ + ミニカート上部に警告バナーを動的挿入/削除する。
     * テーマオリジナルファイルを編集せずに main-cart / mini-cart 内に表示するため、
     * <template id="c-bundle-conflict-banner-template"> から clone して挿入する。
     */
    applyBannerState(violated) {
      if (!this._bannerTemplate) {
        this._bannerTemplate = document.getElementById('c-bundle-conflict-banner-template');
      }

      BANNER_TARGETS.forEach((entry) => {
        const target = entry.resolve ? entry.resolve() : document.querySelector(entry.selector);
        if (!target) return;

        const existing = target.querySelector(':scope > [data-c-bundle-conflict-banner]');

        if (violated) {
          if (existing) return; // 既に挿入済み
          if (!this._bannerTemplate || !this._bannerTemplate.content) return;

          const fragment = this._bannerTemplate.content.cloneNode(true);
          const bannerEl = fragment.querySelector('[data-c-bundle-conflict-banner]');
          if (!bannerEl) return;

          const actionBtn = bannerEl.querySelector('[data-action="show-modal"]');
          if (actionBtn) {
            actionBtn.addEventListener('click', () => {
              this.openedSignature = null;
              this.open(this.lastResult);
            });
          }

          target.insertBefore(bannerEl, target.firstChild);
        } else if (existing) {
          existing.parentNode.removeChild(existing);
        }
      });
    }

    buildEventPayload(result, cart) {
      return {
        groups: result.groupNames.join(','),
        item_count: result.items.length,
        cart_value: cart && typeof cart.total_price === 'number'
          ? cart.total_price / 100
          : null,
        currency: cart && cart.currency ? cart.currency : null,
      };
    }

    sendGA4Event(name, payload) {
      if (!this.enableGa4Events) return;
      const safePayload = payload || {};

      // 方式1: テーマ直接 gtag.js (またはGoogle Tag)
      if (typeof window.gtag === 'function') {
        try {
          window.gtag('event', name, safePayload);
        } catch (_) {}
      }

      // 方式2: GTM dataLayer
      if (Array.isArray(window.dataLayer)) {
        try {
          window.dataLayer.push(Object.assign({ event: name }, safePayload));
        } catch (_) {}
      }

      // 方式3: Shopify Web Pixel (Customer Events)
      // Google & YouTube アプリ + カスタムピクセル経由のGA4で必要
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        try {
          window.Shopify.analytics.publish(name, safePayload);
        } catch (_) {}
      }
    }

    detectViolation(cart) {
      const detectedGroups = new Map();
      const items = [];

      (cart.items || []).forEach((item) => {
        // 除外コレクション (航空便などのオプション加算商品) は判定対象外
        if (this.excludeProductIds.indexOf(item.product_id) !== -1) {
          return;
        }

        const group = this.groups.find(
          (g) => Array.isArray(g.productIds) && g.productIds.indexOf(item.product_id) !== -1
        );
        const key = group ? group.handle : REGULAR_GROUP_KEY;
        const name = group ? group.name : REGULAR_GROUP_NAME;
        if (!detectedGroups.has(key)) detectedGroups.set(key, name);
        items.push({
          title: item.product_title || item.title || '',
          groupName: name,
          groupKey: key,
          imageUrl: (item.featured_image && item.featured_image.url) || item.image || '',
          imageAspectRatio:
            (item.featured_image && item.featured_image.aspect_ratio) || 1,
        });
      });

      const groupKeys = Array.from(detectedGroups.keys()).sort();
      const violated = groupKeys.length >= 2;
      const signature = violated
        ? groupKeys.join('|') + '#' + items.map((i) => i.title).join(',')
        : '';

      return { violated, items, groupNames: Array.from(detectedGroups.values()), signature };
    }

    applyCheckoutLock(violated) {
      // パススルーモード: チェックアウトボタンには一切手を加えない。
      // 違反していても disabled / aria-disabled / .is-locked / title を付与しない。
      // 警告バナーとモーダルだけが残り、購入は通常通り進められる。
      if (!this.enableCheckoutBlock) return;

      document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
        btn.disabled = !!violated;
        btn.setAttribute('aria-disabled', violated ? 'true' : 'false');
        btn.classList.toggle('is-locked', !!violated);
        btn.title = violated ? this.checkoutDisabledLabel : '';
      });
      document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
        el.classList.toggle('is-locked', !!violated);
        if (violated) {
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.removeAttribute('aria-hidden');
        }
      });
    }

    renderConflictList(result) {
      const wrapper = this.querySelector('[data-conflict-wrapper]');
      const titleEl = this.querySelector('[data-conflict-title]');
      const listEl = this.querySelector('[data-conflict-list]');
      if (!wrapper || !listEl) return;

      // 既存リストをクリア (DOM API のみ使用)
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);

      if (!result || !result.items.length) {
        wrapper.hidden = true;
        return;
      }

      if (titleEl) titleEl.textContent = this.conflictListLabel || '';

      const imageSize = this.productImageSize || 'natural';

      result.items.forEach((it) => {
        const li = document.createElement('li');
        li.className = 'c-bundle-conflict-modal__conflict-list-item';

        // 画像ラッパー: テーマの .aspect-ratio パターンを再現し、
        // settings.product_image_size (natural/short/square/tall/portrait) を引き継ぐ
        const imageWrapper = document.createElement('span');
        imageWrapper.className =
          'c-bundle-conflict-modal__conflict-list-item-image-wrapper aspect-ratio' +
          (imageSize !== 'natural' ? ' aspect-ratio--' + imageSize : '');
        imageWrapper.style.setProperty('--aspect-ratio', String(it.imageAspectRatio || 1));

        const img = document.createElement('img');
        img.className = 'c-bundle-conflict-modal__conflict-list-item-image';
        img.alt = '';
        // モーダル内 = 開いた瞬間に見える要素なので eager load。
        // lazy だと intersection observer の判定遅延で初回表示が空枠になるケースがあった。
        img.loading = 'eager';
        if (it.imageUrl) {
          img.src = withImageWidth(it.imageUrl, 128);
        }
        imageWrapper.appendChild(img);
        li.appendChild(imageWrapper);

        // メタ列 (グループラベル + 商品名)
        const meta = document.createElement('div');
        meta.className = 'c-bundle-conflict-modal__conflict-list-item-meta';

        // テーマの product-labels スニペットと同じ構造に揃える:
        // <div class="label-list label-list--horizontal"><span class="label label--subdued">...</span></div>
        // ラッパーを介すことで theme.css 側の余白・整列規約 (label-list--horizontal の gap 等) が効く
        const labelList = document.createElement('div');
        labelList.className = 'label-list label-list--horizontal';

        const groupSpan = document.createElement('span');
        groupSpan.className = 'label label--custom2';
        groupSpan.textContent = it.groupName;
        labelList.appendChild(groupSpan);

        const titleSpan = document.createElement('span');
        titleSpan.className = 'c-bundle-conflict-modal__conflict-list-item-title';
        titleSpan.textContent = it.title;

        meta.appendChild(labelList);
        meta.appendChild(titleSpan);
        li.appendChild(meta);
        listEl.appendChild(li);
      });

      wrapper.hidden = false;
    }

    open(result) {
      if (result) this.renderConflictList(result);
      this.setAttribute('open', '');
      document.documentElement.classList.add('lock-all');
    }

    close() {
      this.removeAttribute('open');
      document.documentElement.classList.remove('lock-all');
    }
  }

  customElements.define('c-bundle-conflict-modal', CBundleConflictModal);
})();
